<template>
  <div class="flex">
    <DropdownModule
      ref="dropdownRef"
      class="flex"
      :width="220"
      append-to-body
      dropdown-z-index="z-100"
      position-menu="right"
      :background="isDarkMode ? 'bg-sidebar' : 'bg-white'"
    >
      <template #button="{isOpen}">
        <div class="inline-flex items-center whitespace-no-wrap w-38 h-38 md:w-auto justify-center md:max-w-full cursor-pointer
        rounded-full md:rounded-8 anim-all px-10 py-10 font-medium text-15 leading-tight text-app-color"
             :class="[
               isOpen ? 'opacity-100' : '',
               isDarkMode ? 'bg-dark-500 opacity-50 hover:opacity-100' : 'bg-grey-light hover:bg-grey-light-hover'
             ]"
        >
          <p class="truncate">{{ firstName }}</p>
          <i class="hidden md:block icon-keyboard-arrow-down text-12 self-center text-app-color ml-5" />
        </div>
      </template>

      <div class="rounded-10 text-14 text-app-color ">
        <div
          v-if="team"
          class="rounded-t-10 hover:bg-selection group-scope flex items-center justify-between px-20 py-9 cursor-pointer border-b"
          :class="isDarkMode ? 'border-project' : 'border-grey-500'"
          @click="switchAccount('team')"
        >
          <div class="truncate mr-10">
            <div class="flex justify-between items-center">
              <p class="text-15 font-bold leading-none mr-15">{{ team.name }}</p>
              <p class="text-12 font-medium">TEAMS</p>
            </div>
            <p class="truncate opacity-70 text-13">{{ user.email }}</p>
          </div>
          <div class="w-20 flex">
            <i v-if="isTeamAccount" class="icon-check_mark text-24" />
          </div>
        </div>

        <div v-if="user.profile.hasPersonal"
             class="hover:bg-selection group-scope flex items-center justify-between px-20 py-9 cursor-pointer text-app-color "
             :class="{'rounded-t-10': !team}"
             @click="switchAccount('Personal')"
        >
          <div class="truncate mr-10">
            <div class="flex justify-between items-center">
              <p class="text-15 font-bold leading-none mr-15">Personal</p>
              <p v-if="!isTeamAccount" class="text-12 font-medium">{{ isFreePlan ? 'FREE' : 'PRO' }}</p>
            </div>
            <p class="truncate opacity-70 text-13">{{ user.email }}</p>
          </div>
          <div class="w-20 flex">
            <i v-if="!isTeamAccount" class="icon-check_mark text-24" />
          </div>
        </div>

        <div class="border-b border-hover">
          <div v-if="isTeamAccount" class="hover:bg-selection group-scope" :class="{'pt-10 pb-6 rounded-t-10': isSharePage}">
            <ButtonModule
              text="Team dashboard"
              :prefix="`icon-team text-20 mr-10 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="text-app-color px-20 py-9 w-full"
              @click="openTeamDashboard"
            />
          </div>

          <div class="hover:bg-selection group-scope" :class="{'pt-10 pb-6 rounded-t-10': isSharePage}">
            <ButtonModule
              text="Settings"
              :prefix="`icon-settings text-20 mr-10 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="text-app-color px-20 py-9 w-full"
              @click="openSettingsModal"
            />
          </div>

          <div class="hover:bg-selection group-scope">
            <ButtonModule
              text=""
              :prefix="`icon-moon text-20 mr-12 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="text-app-color px-20 py-9 w-full"
              @click.stop="updateDesign"
            >
              <div class="flex justify-between w-full">
                <p>Dark mode</p>
                <div class="w-27 h-15 relative rounded-full border-2"
                     :class="[isDarkMode ? 'border-main-color' : 'border-dark-600', isTwilightTheme ? 'group-scope-hover:border-app-color' : '']"
                >
                  <div class="absolute top-1 w-8 h-9 rounded-full transition-left-300"
                       :class="[
                         isDarkMode ? 'bg-main-color' : 'bg-dark-600',
                         isTwilightTheme ? 'group-scope-hover:bg-app-color' : '',
                         isDarkMode ? 'left-13' : 'left-2'
                       ]"
                  />
                </div>
              </div>
            </ButtonModule>
          </div>
          <div v-if="!isSharePage" class="hover:bg-selection group-scope">
            <ButtonModule
              text="Download apps"
              :prefix="`icon-download-apps text-18 mr-12 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
              class="text-app-color px-20 py-9 w-full"
              @click="goToDownloadPage"
            />
          </div>
        </div>

        <div v-if="!isSharePage" class="hover:bg-selection group-scope rounded-b-9">
          <ButtonModule
            text="Log out"
            :prefix="`icon-log-out text-17 mr-14 ${isDarkMode ? 'text-main-color' : ''} ${isTwilightTheme ? 'group-scope-hover:text-app-color' : ''}`"
            class="text-app-color px-20 py-9 w-full"
            @click="logout"
          />
        </div>
      </div>
    </DropdownModule>
  </div>
</template>

<script>
import DropdownModule from '@/components/shared/form-controls/DropdownModule'
import ButtonModule from '@/components/shared/form-controls/ButtonModule'

import { defineComponent, computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'

import { settingsService } from '@/services'
import localStorageService from '@/core/local-storage'
import { updatePreviousThemesLocalStorage, dispatchChangingDesign } from '@/core/helper-functions'

export default defineComponent({
  components: { DropdownModule, ButtonModule },

  setup () {
    const { state, getters, commit, dispatch } = useStore()
    const route = useRoute()
    const router = useRouter()

    const templateImage = ref(null)
    const templateBlob = ref(null)

    const headerDesignRef = ref(null)
    const dropdownRef = ref(null)
    const exportModalRef = ref(null)
    const sendModalRef = ref(null)
    const iframeRef = ref(null)
    const noteInfoRef = ref(null)
    const templateRef = ref(null)

    const words = computed(() => state.project.words)
    const user = computed(() => state.profile.user)
    const team = computed(() => state.team.team)
    const settings = computed(() => state.settings.settings)
    const firstName = computed(() => {
      const name = user.value?.profile?.firstName
      if (isMobile.value) return name.charAt(0).toUpperCase()
      return name
    })
    const isMobile = computed(() => getters.isMobile)
    const isFreePlan = computed(() => getters.isFreePlan)
    const isDarkMode = computed(() => getters.isDarkMode)
    const showSidebar = computed(() => state.settings.showSidebar)
    const isSharePage = computed(() => route.name === 'Share')
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const previousTheme = computed(() => state.settings.previousTheme)
    const currentProject = computed(() => state.project.currentProject)
    const isTwilightTheme = computed(() => getters.isTwilightTheme)
    const previousDarkTheme = computed(() => state.settings.previousDarkTheme)

    function close () {
      dropdownRef.value.close()
    }

    function changeDesign () {
      setTimeout(() => {
        close()

        if (isMobile.value) {
          commit('changeActiveState', 'Design')
          commit('toggleSettingsModal', true)
          commit('toggleAside', false)
          commit('updateBackButton', false)
        } else {
          headerDesignRef.value.open()
        }
      }, 10)
    }

    function openSettingsModal () {
      commit('toggleSettingsModal', true)
    }

    function openTeamDashboard () {
      if (isMobile.value) {
        commit('toggleAside', false)
        commit('updateBackButton', false)
      }
      commit('changeActiveState', 'TeamMembers')
      commit('toggleSettingsModal', true)
    }

    function updateDesign () {
      let theme = ''
      const previousThemes = localStorageService.getItem('previous-themes') || {}
      const userPreviousThemes = previousThemes[user.value?.id] || {}

      if ((!settings.value.theme.includes('Dark')) && (settings.value.theme !== 'Dracula')) {
        theme = previousDarkTheme.value || userPreviousThemes?.dark || 'Dark Orange'
        commit('updatePreviousTheme', settings.value.theme)
        updatePreviousThemesLocalStorage(user.value, 'light', settings.value.theme)
      } else {
        theme = previousTheme.value || userPreviousThemes?.light || 'Magenta'
        commit('updatePreviousDarkTheme', settings.value.theme)
        updatePreviousThemesLocalStorage(user.value, 'dark', settings.value.theme)
      }

      const data = { ...settings.value, theme: theme }

      commit('updateSettings', data)
      settingsService.update(data)
      dispatchChangingDesign(data)
    }

    function switchAccount (to) {
      commit('setProjects', [])
      commit('setTemplates', [])

      if (to === 'Personal') {
        if (!isTeamAccount.value) return
        commit('setTeamId', null)
        localStorageService.setItem('account-type', '')
        dispatch('getGlobalData').then(() => router.push({ name: 'Books' }))
      } else {
        if (isTeamAccount.value) return
        commit('setTeamId', team.value.id)
        localStorageService.setItem('account-type', team.value.link)
        dispatch('getGlobalData').then(() => router.push({ name: 'TeamBooks', params: { team: team.value.link } }))
      }

      close()
    }

    function logout () {
      commit('toggleSettingsModal', false)
      localStorageService.removeItem('lastOpenedProject')
      router.push({ name: 'Login' })
    }

    function goToDownloadPage () {
      window.open('https://helloivy.co/downloads', '_blank')
    }

    return {
      templateBlob,
      templateImage,
      // refs
      iframeRef,
      dropdownRef,
      templateRef,
      noteInfoRef,
      sendModalRef,
      exportModalRef,
      headerDesignRef,
      // computed
      user,
      team,
      words,
      firstName,
      settings,
      isMobile,
      isDarkMode,
      isFreePlan,
      isSharePage,
      showSidebar,
      isTeamAccount,
      currentProject,
      isTwilightTheme,
      // functions
      logout,
      updateDesign,
      changeDesign,
      switchAccount,
      goToDownloadPage,
      openSettingsModal,
      openTeamDashboard
    }
  }
})
</script>
